import React from "react";
import PropTypes from "prop-types";
import { OutlinedInput, InputAdornment } from "@mui/material";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";

/**
 * CommonInput Component
 * Centralized, reusable outlined input component adhering to AutoBET design tokens.
 *
 * Features:
 * - Clean OutlinedInput with hidden notched outline and subtle card-flat shadow
 * - Built-in focus states with theme primary border and glowing focus shadow
 * - Transparent input area with reset browser outlines/borders
 * - Automatic wrapping of `startAdornment` and `endAdornment` in InputAdornment
 * - Flexible sizing ('small' | 'medium') and responsive width control
 */
const CommonInput = ({
  value,
  onChange,
  placeholder,
  type = "text",
  size = "small",
  fullWidth = false,
  width,
  startAdornment,
  endAdornment,
  disabled = false,
  readOnly = false,
  error = false,
  sx = {},
  inputProps = {},
  ...props
}) => {
  const resolvedStartAdornment = startAdornment
    ? React.isValidElement(startAdornment) && startAdornment.type === InputAdornment
      ? startAdornment
      : <InputAdornment position="start">{startAdornment}</InputAdornment>
    : undefined;

  const resolvedEndAdornment = endAdornment
    ? React.isValidElement(endAdornment) && endAdornment.type === InputAdornment
      ? endAdornment
      : <InputAdornment position="end">{endAdornment}</InputAdornment>
    : undefined;

  const resolvedWidth = fullWidth ? "100%" : width;

  return (
    <OutlinedInput
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      fullWidth={fullWidth}
      disabled={disabled}
      readOnly={readOnly}
      error={error}
      startAdornment={resolvedStartAdornment}
      endAdornment={resolvedEndAdornment}
      sx={{
        bgcolor: disabled ? colors.slate[100] : colors.background.paper,
        borderRadius: borderRadiusPx.xl,
        ...(resolvedWidth ? { width: resolvedWidth } : {}),
        boxShadow: customShadows.cardFlat,
        transition: "all 0.2s ease-in-out",
        border: `1px solid ${error ? colors.error?.main || "#ef4444" : colors.neutral.border}`,
        "& .MuiOutlinedInput-notchedOutline": {
          display: "none",
        },
        "&:hover": {
          borderColor: error
            ? colors.error?.dark || "#dc2626"
            : colors.neutral.borderHover || colors.slate[300],
        },
        "&.Mui-focused": {
          borderColor: error
            ? colors.error?.main || "#ef4444"
            : colors.primary.main,
          boxShadow: error
            ? "0 4px 12px rgba(239, 68, 68, 0.15)"
            : `0 4px 12px ${colors.primary.subtle}`,
        },
        input: {
          py: size === "small" ? 1.25 : 1.5,
          fontSize: "14px",
          color: colors.text.primary,
          border: "none !important",
          outline: "none !important",
          boxShadow: "none !important",
          background: "transparent !important",
          "&:focus": {
            border: "none !important",
            outline: "none !important",
            boxShadow: "none !important",
          },
          ...inputProps,
        },
        ...sx,
      }}
      {...props}
    />
  );
};

CommonInput.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  fullWidth: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  sx: PropTypes.object,
  inputProps: PropTypes.object,
};

export default CommonInput;
